// Detecção PURA dos carimbos do auto-advance em cascata (defeito consertado em
// 27/09/2026 — ver docs/changelog/tv-series-autoadvance-gravador-em-lote.md).
//
// O defeito gravava o `currentTime`/`duration` do episódio que TERMINOU em vários
// episódios seguintes da mesma série, todos como `finished`, numa rajada de poucos
// segundos. Medido no aparelho do dono:
//   GoT 7823       3188.744 / 3208.107   lote de 7s
//   Expresso 6962  3144.486 / 3163.83    lote de 5s
//   Silo 6212      3208.89  / 3228.416   lote de 1s
//
// ── A ASSINATURA (cada critério tem um caso de teste que só ele rejeita) ──
//   1. mesmo scope, mesma série, ≥ 2 episódios DIFERENTES;
//   2. pos E dur IDÊNTICOS no valor EXATO do número (a fração inteira, não o
//      segundo). `currentTime` é contínuo e amostrado em instante arbitrário: dois
//      episódios reais não terminam no mesmo milésimo. Durações podem coincidir até
//      o milésimo (mesmo encoder); POSIÇÕES não — e é a posição que separa uma
//      maratona de um carimbo;
//   3. `dur − pos` na janela do auto-advance, [15, 31]s. Derivação: o aviso arma a
//      ≤ 30s do fim (`NEXT_EPISODE_TRIGGER_SEC`) e avança depois de 10 ticks de ≥ 1s,
//      ou seja a ≤ ~20s; com o vídeo em buffering durante a contagem o `currentTime`
//      anda menos e o avanço acontece mais perto de 30s. Piso de 15s cobre deriva de
//      tick em motor lento. Os três lotes medidos: 19.36 / 19.34 / 19.53;
//   4. `lastWatchedAt` do grupo numa RAJADA de ≤ 120s (a cascata grava tudo num
//      flush só; os lotes medidos duraram 1-7s). Uma maratona real grava um episódio
//      a cada ~45min;
//   5. só sai o que está `finished` (é o `finished` falso que o carimbo causa).
//
// ── QUAL FICA ──
// O carimbo carrega o pos/dur do episódio de ORIGEM (o que terminou de verdade). O
// registro da origem é o MAIS ANTIGO do lote, quando está no lote: o cleanup dele roda
// primeiro na cascata, e o `lastWatchedAt` só sobe dali. Mas ele pode NÃO estar: a
// origem do Expresso é o T1E1, fora dos 38 carimbos, e a do GoT (T2E1) nunca chegou
// ao servidor nem ao IDB. E o relógio é em SEGUNDOS: medido pelo dba, o menor segundo
// tem 3 carimbos no GoT, 4 no Expresso, 7 no Silo de 22/09 e os 12 no Silo de 15/08
// (o lote inteiro num segundo). O id do episódio NÃO desempata: o Xtream numera por
// importação (T1E10 = 9001 importado depois de T2E1 = 4001), e desempatar por id
// apagava o episódio real (review de 28/09/2026, sonda executada).
//
// Por isso quem decide é a DURAÇÃO REAL do episódio (`info.duration_secs` do
// series_info), quando ela é conhecida, e SÓ COM EVIDÊNCIA de onde a origem está. Os
// candidatos a origem são os episódios da SÉRIE (do catálogo) cuja duração bate com o
// `dur` do lote (± STAMP_DURATION_TOLERANCE_SEC). Do segundo mais antigo só sai quem tem
// duração real CONHECIDA e DIFERENTE, e só quando:
//   (a) um candidato está ENTRE os próprios empatados (a origem está no lote: os Silos);
//   (b) ou um candidato tem registro PRÓPRIO fora do lote, com o `dur` batendo (a
//       origem está fora e existe: o T1E1 do Expresso).
// Sem (a) nem (b), fecha `conservative` (ficam todos). A coincidência de duração com
// um episódio sem registro nenhum não é evidência: com o catálogo da origem errado, ela
// apagava a origem (review de 28/09/2026, sonda executada). No GoT, cujo T2E1 não tem
// registro, isso mantém os 3 do menor segundo: custo aceito. Fica também quem bate (a
// origem, ou uma coincidência dentro do lote) e quem não tem duração no catálogo. Se
// NENHUM episódio da série tem a duração do lote, o dado do painel não explica o lote:
// ficam todos.
//
// Sem a duração real: um registro só no segundo mais antigo fica (a origem, ou um
// carimbo, o erro seguro); empatados desempatam pela ordem da série (`seasonNumber` +
// `episodeNumber`) se TODOS a tiverem; senão o empate fica PENDENTE (todos ficam, e a
// migração busca a duração no boot seguinte). Os dos segundos seguintes saem sempre.
//
// O erro fica pro lado de um ✓ a mais. O caso que ainda tira um ✓ legítimo exige duas
// coisas juntas: o catálogo da origem errado em mais de 2s E outro episódio empatado com
// ela (ou com registro próprio) durando o mesmo que ela ± 2s.
//
// O ✓ a mais não é só visual: o "Continuar" ancora no episódio tocado MAIS RECENTE, e o
// carimbo mantido é o mais recente quando nada foi assistido depois dele. Quem parou no
// e1 com o e2..e4 mantidos vê o "Continuar" apontar o e3 (medido, sonda SKIP do review
// de 29/09/2026), pulando o e2, que nunca viu. É permanente: o `finished` é monotônico
// (só desmarcar os mantidos na gaveta de episódios desfaz). Na TV do Fernando não
// acontece: no GoT, o T2E7 re-assistido de verdade em 04/09 é mais recente que os 3
// mantidos, e é ele a âncora. A regra não muda por isso: é o custo de não apagar sem
// evidência.

/** Janela `dur − pos` do instante do auto-advance, em segundos (ver cabeçalho, item 3). */
export const STAMP_REMAINING_MIN_SEC = 15
export const STAMP_REMAINING_MAX_SEC = 31
/** Largura máxima da rajada de `lastWatchedAt` num lote, em segundos (item 4). */
export const STAMP_BURST_MAX_SEC = 120
/**
 * Tolerância entre o `dur` gravado pelo `<video>` e o `duration_secs` do Xtream, em
 * segundos. São medidas diferentes: o `<video>` dá a duração do container com fração;
 * o Xtream, metadado do provedor em segundos inteiros. Calibração (dba, 28/09/2026,
 * leitura): o T2E7 da GoT (`807946`) deu 3310,059 no player contra 3310 no payload
 * (Δ 0,06s); o T2E4 da 5992, 3041 contra 3041; e a origem do Expresso (`765692`), 3163,83
 * igual ao `dur` do lote e estável até 0,01s entre duas sessões. O maior Δ medido é
 * 0,06s; o arredondamento de um inteiro vale até 1s. 2s cobre os dois com folga, e o
 * erro de passar dela é o seguro (a origem não achada vira `conservative`: ficam todos).
 */
export const STAMP_DURATION_TOLERANCE_SEC = 2

/** Duração real (s) de cada episódio da série, por id de episódio. */
export type EpisodeDurations = ReadonlyMap<string, number>
/** Durações da série do grupo, ou `undefined` se não há (ainda) como saber. */
export type DurationLookup = (providerScope: string, seriesId: string) => EpisodeDurations | undefined

/** Como o empate do segundo mais antigo foi decidido (ver "QUAL FICA"). */
export type TieResolution =
  /** Sem empate: um registro só no segundo mais antigo. */
  | 'none'
  /** Todos os empatados tinham a ordem da série. */
  | 'series-order'
  /** Decidido pela duração real dos episódios. */
  | 'duration'
  /** As durações existem mas nenhuma explica o lote: ficam todos. */
  | 'conservative'
  /** Sem as durações: ficam todos, e o grupo espera o próximo boot. */
  | 'pending'

/** As durações de episódio de um `series_info` (`seasons[k][i].info.duration_secs`).
 *  Só entram valores finitos e > 0. Mapa vazio = a série não tem durações. */
export function episodeDurationsFrom(
  seasons: Readonly<Record<string, ReadonlyArray<{ id: string; info?: { duration_secs?: unknown } | null }>>> | null | undefined,
): Map<string, number> {
  const out = new Map<string, number>()
  if (!seasons) return out
  for (const eps of Object.values(seasons)) {
    if (!Array.isArray(eps)) continue
    for (const ep of eps) {
      const d = Number(ep?.info?.duration_secs)
      if (ep && typeof ep.id === 'string' && Number.isFinite(d) && d > 0) out.set(ep.id, d)
    }
  }
  return out
}

/** Subconjunto de PlaybackPosition que a detecção lê (estrutural, testável). */
export interface StampCandidate {
  /** Id LOCAL completo (`scope::series-episode:S:E`). */
  id: string
  providerScope: string
  type: string
  seriesId?: string | undefined
  episodeId?: string | undefined
  positionSec: number
  durationSec: number
  /** Época em SEGUNDOS (como o player grava). */
  lastWatchedAt: number
  finished?: boolean | undefined
  /** Ordem da série, quando o registro a tem (o "Já vi"/auto-advance grava; o save de
   *  posição do player, não). Só desempata se TODOS os empatados a tiverem. */
  seasonNumber?: number | undefined
  episodeNumber?: number | undefined
}

export interface StampGroup {
  providerScope: string
  seriesId: string
  positionSec: number
  durationSec: number
  /** Os registros mantidos do segundo mais antigo (ver "QUAL FICA"). VAZIO quando a
   *  duração real mostra que a origem não está no lote (todos são carimbos). */
  keepIds: string[]
  /** Como o empate foi decidido. `pending` = falta a duração real: o grupo volta. */
  tie: TieResolution
  /** Os carimbos a apagar. */
  removeIds: string[]
  /** Maior `lastWatchedAt` do lote (segundos): o fim da rajada. Âncora do relógio
   *  do tombstone (ver a migração). */
  burstEndSec: number
}

/** Chave de agrupamento: valor EXATO de pos/dur (`String(number)` é ida-e-volta exata). */
function groupKey(c: StampCandidate): string {
  return `${c.providerScope}|${c.seriesId}|${String(c.positionSec)}|${String(c.durationSec)}`
}

/** Posição na ordem da série, ou null se o registro não a tem. */
function seriesOrder(c: StampCandidate): number | null {
  const s = c.seasonNumber
  const e = c.episodeNumber
  if (typeof s !== 'number' || typeof e !== 'number' || !Number.isFinite(s) || !Number.isFinite(e)) return null
  return s * 100_000 + e
}

/**
 * Os registros que ficam de um grupo, e como o empate foi decidido (ver "QUAL FICA").
 * Só o segundo mais antigo pode ter a origem; os dos segundos seguintes são carimbos.
 */
function keptOf(
  members: ReadonlyArray<StampCandidate>,
  durations: EpisodeDurations | undefined,
  sameSeries: ReadonlyArray<StampCandidate>,
): { kept: StampCandidate[]; tie: TieResolution } {
  let oldest = Number.POSITIVE_INFINITY
  for (const m of members) if (m.lastWatchedAt < oldest) oldest = m.lastWatchedAt
  const tied = members.filter((m) => m.lastWatchedAt === oldest)
  // Sem a duração real: o segundo mais antigo fica. Único, ele é a origem ou (se a
  // origem saiu do lote) um carimbo, o erro seguro. Empatado, desempata pela ordem da
  // série se todos a tiverem; senão fica PENDENTE (a migração busca a duração).
  // `undefined` = ainda não se sabe. Mapa VAZIO = o catálogo respondeu sem durações:
  // cai no `conservative` abaixo (a migração só manda o vazio depois de insistir).
  if (durations === undefined) {
    if (tied.length === 1) return { kept: tied, tie: 'none' }
    const orders = tied.map(seriesOrder)
    if (orders.every((o) => o !== null)) {
      const first = Math.min(...(orders as number[]))
      return { kept: tied.filter((_, i) => orders[i] === first), tie: 'series-order' }
    }
    return { kept: tied, tie: 'pending' }
  }
  // Com a duração real, ELA decide, mesmo com um registro só no segundo mais antigo:
  // se a origem está fora do lote (o Expresso: a origem é o T1E1, fora dos 38), o
  // mais antigo é um carimbo, e mantê-lo seria um ✓ falso afirmado sem necessidade.
  // A origem é o episódio da SÉRIE (não do lote) cuja duração real bate com o `dur`.
  const dur = tied[0]!.durationSec
  const origins = new Set<string>()
  for (const [episodeId, real] of durations) {
    if (Math.abs(real - dur) <= STAMP_DURATION_TOLERANCE_SEC) origins.add(episodeId)
  }
  if (origins.size === 0) return { kept: tied, tie: 'conservative' }
  // Evidência de onde a origem está (ver "QUAL FICA"): (a) no empate, ou (b) com
  // registro próprio fora do lote e o `dur` batendo. Sem ela, não se afirma nada.
  const originInTie = tied.some((m) => origins.has(m.episodeId!))
  const memberIds = new Set(members.map((m) => m.id))
  const originOutside = sameSeries.some(
    (r) =>
      !memberIds.has(r.id) &&
      r.episodeId !== undefined &&
      origins.has(r.episodeId) &&
      Math.abs(r.durationSec - dur) <= STAMP_DURATION_TOLERANCE_SEC,
  )
  if (!originInTie && !originOutside) return { kept: tied, tie: 'conservative' }
  // Sai só quem tem duração real CONHECIDA e diferente: esse não pode ser a origem.
  const kept = tied.filter((m) => !durations.has(m.episodeId!) || origins.has(m.episodeId!))
  return { kept, tie: 'duration' }
}

/** Registro elegível a entrar num grupo (critérios que valem por registro). */
function isEligible(c: StampCandidate): boolean {
  if (c.type !== 'series-episode') return false
  if (!c.seriesId || !c.episodeId) return false
  if (!Number.isFinite(c.positionSec) || !Number.isFinite(c.durationSec) || c.durationSec <= 0) return false
  if (!Number.isFinite(c.lastWatchedAt)) return false
  const remaining = c.durationSec - c.positionSec
  return remaining >= STAMP_REMAINING_MIN_SEC && remaining <= STAMP_REMAINING_MAX_SEC
}

/**
 * Acha os lotes de carimbo. PURO: não lê nem grava nada. Ordem da saída estável
 * (por scope, série e valor), e `removeIds` em ordem de id.
 */
export function findAutoAdvanceStamps(
  records: ReadonlyArray<StampCandidate>,
  durationsOf: DurationLookup = () => undefined,
): StampGroup[] {
  const groups = new Map<string, StampCandidate[]>()
  const seenIds = new Set<string>()
  // Todos os registros de cada série (não só os elegíveis): é onde se procura o
  // registro próprio da origem fora do lote (evidência (b) do "QUAL FICA").
  const bySeries = new Map<string, StampCandidate[]>()
  for (const r of records) {
    if (seenIds.has(r.id)) continue // o mesmo registro vindo do IDB e do fallback
    seenIds.add(r.id)
    if (r.type === 'series-episode' && r.seriesId && r.episodeId && Number.isFinite(r.durationSec)) {
      const sk = `${r.providerScope}|${r.seriesId}`
      const list = bySeries.get(sk)
      if (list) list.push(r)
      else bySeries.set(sk, [r])
    }
    if (!isEligible(r)) continue
    const k = groupKey(r)
    const list = groups.get(k)
    if (list) list.push(r)
    else groups.set(k, [r])
  }

  const out: StampGroup[] = []
  for (const k of [...groups.keys()].sort()) {
    const members = groups.get(k)!
    const episodes = new Set(members.map((m) => m.episodeId))
    if (episodes.size < 2) continue
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const m of members) {
      if (m.lastWatchedAt < min) min = m.lastWatchedAt
      if (m.lastWatchedAt > max) max = m.lastWatchedAt
    }
    if (max - min > STAMP_BURST_MAX_SEC) continue
    const head = members[0]!
    const sameSeries = bySeries.get(`${head.providerScope}|${head.seriesId}`) ?? []
    const { kept, tie } = keptOf(members, durationsOf(head.providerScope, head.seriesId!), sameSeries)
    const keptIds = new Set(kept.map((m) => m.id))
    const removeIds = members
      .filter((m) => !keptIds.has(m.id) && m.finished === true)
      .map((m) => m.id)
      .sort()
    // Grupo pendente fica na saída mesmo sem nada a apagar agora: é o que diz à
    // migração que ainda não terminou (e de quais séries buscar a duração).
    if (removeIds.length === 0 && tie !== 'pending') continue
    out.push({
      providerScope: head.providerScope,
      seriesId: head.seriesId!,
      positionSec: head.positionSec,
      durationSec: head.durationSec,
      keepIds: [...keptIds].sort(),
      tie,
      removeIds,
      burstEndSec: max,
    })
  }
  return out
}
